<template>
  <SessionSlide
    ref="slide"
    :id="'slide' + props.index"
    visual-position="fixed"
    :class="{
      'slide-after-activity': !isDisplayed,
      'submitted-mapping': isDisplayed,
    }"
    :data-test="`session-slide-${index}`"
  >
    <template #text>
      <SlideText :text="text" />
      <div class="cta-button-wrapper">
        <SessionCompleteButton
          v-if="isCompleteButton"
          :buttonProps="{
            ready,
            canCompletePathwayStep,
            completed,
            currentStepWasAlreadyComplete,
            isForPathwayStep: true,
            completeButtonText,
          }"
          :show-close-button="showCloseButton"
          @close="close"
          @complete="handleComplete"
        />
      </div>
      <Modal
        v-model="isGoalModalDisplayed"
        title="Change Goal?"
        :append-to-body="true"
        size="sm"
        data-test="change-outcome-modal"
      >
        <template #title>
          <h2 class="text-center">Change Pathway?</h2>
        </template>
        <p v-if="!!pathway" data-test="change-outcome-text">
          Completing this step will make
          <span style="font-weight: bold">{{ cms.asText(pathway.data.title) }}</span> your active
          Personal Pathway<span
            v-if="!!csContext.userProfile.currentPersonalPathwayUid"
            data-test="has-existing-pathway-text"
            >, and will affect any existing Pathway progress you may have</span
          >. Are you sure you want to do this?
        </p>
        <template #footer>
          <div>
            <button
              type="button"
              class="btn btn-default"
              @click="handleModalHide('cancel')"
              data-test="goal-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleModalHide('ok')"
              data-test="goal-modal-confirm"
            >
              Yes
            </button>
          </div>
        </template>
      </Modal>
    </template>
    <template #visual>
      <VisualPositioningCanvas>
        <CmsImg v-if="!!image.url" ref="visual" class="slide-image" :src="image" />
        <CmsLottieAnimation
          v-if="lottieSrc"
          ref="visual"
          :id="'lottie-' + props.index"
          :container-id="'slide' + props.index"
          :src="lottieSrc"
        />
      </VisualPositioningCanvas>
    </template>
  </SessionSlide>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch, watchEffect } from "vue";
import axios from "axios";
import { Modal } from "uiv";
import { useRoute } from "vue-router";
import SessionSlide from "../../sessions/session-slide.vue";
import VisualPositioningCanvas from "../../sessions/visual-positioning-canvas.vue";
import CmsImg from "../cms-img.vue";
import SessionCompleteButton from "../session-complete-button.vue";
import CmsLottieAnimation from "../cms-lottie-animation.vue";
import {
  CmsPersonalPathway,
  CmsPersonalPathwayStep,
  CmsPersonalPathwayStepData,
  CmsTeamPathway,
  CmsTeamPathwayStep,
  CmsTeamPathwayStepData,
} from "../../../services/cms/cms-type-definitions";
import { useFinalPathwayStepSlice } from "../../../composables/useFinalPathwayStepSlice";
import {
  PersonalPathwayUserProgressApiModel,
  PersonalPathwayCompletedStepApiModel,
  TeamMemberCompletedPathwayStepApiModel,
  TeamPathwayApiModel,
  UserTeamPermissionsApiModel,
} from "../../../api-models";
import { useAxios } from "../../../composables/api";
import cms from "../../../services/cms/cms";
import { csContext } from "../../../services/cs-context";
import delay from "../../../services/utilities/delay";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import SlideText from "./slide-text.vue";
import { useToggle } from "../../../composables/useToggle";

type PathwayCompletions = { [key: string]: any | null };

const props = defineProps<{
  doc: CmsTeamPathwayStep | CmsPersonalPathwayStep;
  index: number;
  isAfterStrengthMapping: boolean;
}>();

const error = ref(false);
const isInTeam = ref(false);
const pathwayIsCurrent = ref(false);
const pathway = ref<CmsTeamPathway | CmsPersonalPathway | null>(null);
const stepIndex = ref<number | null>(null);
const completed = ref(false);
const currentStepWasAlreadyComplete = ref(false);
const previousStepsCompleted = ref(false);
const completedPathwaySteps = ref<PathwayCompletions | null>(null);
const ready = ref(false);
const isGoalModalDisplayed = ref(false);

const cmsSessionStore = useCmsSessionStore(pinia());

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

watchEffect(() => {
  if (!isDisplayed.value && cmsSessionStore.hasFinishedMapping) {
    toggleIsDisplayed();
  }
});

const pathwayStep = computed<CmsTeamPathwayStepData | CmsPersonalPathwayStepData>(() => {
  return props.doc.data;
});

const { text, image, lottieSrc, buttonText, buttonLink, isCompleteButton, showCloseButton } =
  useFinalPathwayStepSlice(pathwayStep.value);

// const hasVisual = computed(() => !!image.value?.url || !!lottieSrc.value);

const isTeamMember = (groupId: string): void => {
  const { promise } = useAxios<UserTeamPermissionsApiModel>(`/api/teamuserpermissions/${groupId}`);
  promise.then((teamPermissions) => {
    isInTeam.value = teamPermissions?.isMember ?? false;
  });
};

const selectPathway = async (): Promise<unknown> => {
  if (!pathway.value) return;

  const selectedPathway: PersonalPathwayUserProgressApiModel = {
    userId: csContext.user.id,
    cmsPersonalOutcomeId: pathway.value.data.goal_relationship.id,
    cmsPersonalOutcomeUid: pathway.value.data.goal_relationship.uid,
    cmsPersonalOutcomeTitle: cms.asText(pathway.value.data.goal_relationship.data?.full_title),
    cmsPersonalPathwayId: pathway.value.id,
    cmsPersonalPathwayUid: pathway.value.uid!,
    cmsPersonalPathwayTitle: cms.asText(pathway.value.data.title),
    isCurrent: true,
    totalPathwayStepCount: pathway.value.data.pathway_steps.length,
    isCompleted: false,
  };

  const { promise } = useAxios("/api/personalpathway", {
    httpMethod: "POST",
    data: selectedPathway,
  });

  return promise;
};

const getCompletedStepsApiUrl = (pathwayId: string, stepId: string, type: string): string => {
  if (type === "cms-personal-pathway") {
    return `/api/personalcompletedpathwaystep?cmsStepId=${stepId}&cmsPathwayId=${pathwayId}`;
  }
  const teamId = csContext.currentTeam?.id;
  return `/api/TeamMemberCompletedPathwayStep?cmsStepId=${stepId}&cmsPathwayId=${pathwayId}&teamId=${teamId}`;
};

const getPathwayStepCompletions = async <
  T extends TeamMemberCompletedPathwayStepApiModel | PersonalPathwayCompletedStepApiModel,
>(
  cmsPathway: CmsTeamPathway | CmsPersonalPathway,
): Promise<{
  [key: string]: T | null;
}> => {
  const pathwaySteps = {} as { [key: string]: T | null };

  await Promise.all(
    cmsPathway.data.pathway_steps.map((step) =>
      axios
        .get(getCompletedStepsApiUrl(cmsPathway.id, step.pathway_step.id, cmsPathway.type))
        .then((value) => {
          pathwaySteps[step.pathway_step.id] = value.data || null;
        }),
    ),
  );

  return pathwaySteps;
};

const setPathwayData = async (
  cmsPathway: CmsTeamPathway | CmsPersonalPathway,
  pathwayApiModel: TeamPathwayApiModel | PersonalPathwayUserProgressApiModel | null,
): Promise<void> => {
  let pathwayStepIndex = null as null | number;

  let pathwayStepCompletions;

  if (cmsPathway.type === "cms-personal-pathway") {
    pathwayStepCompletions = await getPathwayStepCompletions<PersonalPathwayCompletedStepApiModel>(
      cmsPathway,
    );
  } else {
    pathwayStepCompletions =
      await getPathwayStepCompletions<TeamMemberCompletedPathwayStepApiModel>(cmsPathway);
  }

  let previousStepsAreComplete = true;
  let currentStepIsComplete = false;

  for (let i = 0; i < cmsPathway.data.pathway_steps.length; i++) {
    const step = cmsPathway.data.pathway_steps[i].pathway_step;

    if (step.id === props.doc.id) {
      pathwayStepIndex = i;
      currentStepIsComplete = !!pathwayStepCompletions[step.id];
      break;
    }

    if (!pathwayStepCompletions[step.id]) {
      previousStepsAreComplete = false;
    }
  }

  pathway.value = cmsPathway;
  stepIndex.value = pathwayStepIndex;

  if (!pathwayApiModel) {
    pathwayIsCurrent.value = false;
  } else if ("cmsPathwayId" in pathwayApiModel) {
    pathwayIsCurrent.value =
      pathwayApiModel.cmsPathwayId === cmsPathway.id ? pathwayApiModel.isCurrent : false;
  } else {
    pathwayIsCurrent.value =
      pathwayApiModel.cmsPersonalPathwayId === cmsPathway.id ? pathwayApiModel.isCurrent : false;
  }

  currentStepWasAlreadyComplete.value = currentStepIsComplete;
  completed.value = currentStepIsComplete;
  previousStepsCompleted.value = previousStepsAreComplete;
  completedPathwaySteps.value = pathwayStepCompletions;
};

const loadPathwayData = async (): Promise<void> => {
  if (props.doc.type !== "cms-pathway-step" && props.doc.type !== "cms-personal-pathway-step") {
    throw new Error(
      "Can only call getPathwayStep on a cms-pathway-step or cms-personal-pathway-step",
    );
  }

  const pathwayUrl =
    props.doc.type === "cms-personal-pathway-step"
      ? "/api/personalpathway/current"
      : `/api/teampathway/${csContext.currentTeam?.id}`;

  if (props.doc.type === "cms-personal-pathway-step") {
    const pathwayApiModel = (await axios.get(pathwayUrl)).data
      ?.currentPathway as PersonalPathwayUserProgressApiModel | null;

    const pathways = await cms.getAll("cms-personal-pathway");

    const cmsPathway =
      pathways.find(
        (p) =>
          p.id === pathwayApiModel?.cmsPersonalPathwayId &&
          p.data.pathway_steps.some((s) => s.pathway_step.id === props.doc.id),
      ) ??
      pathways.find((p) => p.data.pathway_steps.some((s) => s.pathway_step.id === props.doc.id));

    // This step does not have a pathway.
    if (!cmsPathway) {
      return;
    }
    await setPathwayData(cmsPathway, pathwayApiModel);
  }

  if (props.doc.type === "cms-pathway-step") {
    const pathwayApiModel = (await axios.get(pathwayUrl)).data?.teamPathway as TeamPathwayApiModel;

    if (!pathwayApiModel) {
      return;
    }

    const pathways = await cms.getAll("cms-pathway");

    const cmsPathway =
      pathways.find(
        (p) =>
          p.id === pathwayApiModel.cmsPathwayId &&
          p.data.pathway_steps.some((s) => s.pathway_step.id === props.doc.id),
      ) ??
      pathways.find((p) => p.data.pathway_steps.some((s) => s.pathway_step.id === props.doc.id));

    // This step does not have a pathway.
    if (!cmsPathway) {
      return;
    }
    await setPathwayData(cmsPathway, pathwayApiModel);
  }
};

const personalCompletionApiModel = computed<PersonalPathwayCompletedStepApiModel | null>(() => {
  if (stepIndex.value == null) {
    return null;
  }

  if (!pathway.value) {
    return null;
  }

  if (!props.doc.uid) {
    throw new Error("Docs must have UIDs");
  }

  if (!pathwayIsCurrent.value) {
    return null;
  }

  const outcomeTitle = cms.asText(pathway.value.data.goal_relationship.data?.full_title);

  return {
    cmsStepId: props.doc.id,
    cmsStepUid: props.doc.uid,
    cmsStepTitle: cms.asText(props.doc.data.title),
    stepIndex: stepIndex.value,
    cmsPersonalPathwayId: pathway.value.id,
    cmsPersonalOutcomeTitle: outcomeTitle,
    personalPathwayIsCurrent: true,
  } as PersonalPathwayCompletedStepApiModel;
});

const teamCompletionApiModel = computed<TeamMemberCompletedPathwayStepApiModel | null>(() => {
  if (stepIndex.value == null) {
    return null;
  }

  if (!pathway.value) {
    return null;
  }

  if (!props.doc.uid) {
    throw new Error("Docs must have UIDs");
  }

  if (!csContext.currentTeam?.id) {
    return null;
  }
  if (!pathwayIsCurrent.value) {
    return null;
  }

  const goalTitle = cms.asText(pathway.value.data.goal_relationship.data?.full_title);

  return {
    stepIndex: stepIndex.value,
    cmsPathwayId: pathway.value.id,
    cmsStepId: props.doc.id,
    cmsStepUid: props.doc.uid,
    cmsStepTitle: cms.asText(props.doc.data.title),
    teamId: csContext.currentTeam.id,
    teamPathwayIsCurrent: true,
    teamPathwayIsComplete: false,
    cmsOutcomeTitle: goalTitle,
    hasViewedCompletePage: false,
  };
});

const isLastStepOfPathway = computed<boolean>(() => {
  if (pathway.value) {
    return (
      pathway.value.data.pathway_steps[(pathway.value?.data.pathway_steps.length ?? 1) - 1]
        .pathway_step.id === props.doc.id
    );
  }
  return false;
});

const canCompletePathwayStep = computed<boolean>(() => {
  return (
    (props.doc.type === "cms-personal-pathway-step" &&
      !!personalCompletionApiModel.value &&
      previousStepsCompleted.value &&
      !completed.value &&
      pathwayIsCurrent.value) ||
    (props.doc.type === "cms-personal-pathway-step" &&
      !pathwayIsCurrent.value &&
      stepIndex.value === 0) ||
    (props.doc.type === "cms-pathway-step" &&
      !!teamCompletionApiModel.value &&
      previousStepsCompleted.value &&
      !completed.value &&
      isInTeam.value)
  );
});

const logAsCompleted = async (): Promise<void> => {
  if (props.doc.type === "cms-pathway-step") {
    await axios.post("/api/TeamMemberCompletedPathwayStep", teamCompletionApiModel.value);
  }
  if (props.doc.type === "cms-personal-pathway-step") {
    await axios.post("/api/personalcompletedpathwaystep", personalCompletionApiModel.value);
  }
};

const route = useRoute();
const levelTitle = computed(() => route.query.levelTitle as string);

const closeLocation = (): string => {
  // Check to see if user is a team lead and route accoridingly
  if (levelTitle.value?.length > 0) {
    return "/team/dashboard";
  }

  if (
    (props.doc.type === "cms-pathway-step" || props.doc.type === "cms-personal-pathway-step") &&
    pathway.value
  ) {
    return cms.linkResolver(pathway.value);
  }

  if (buttonLink) {
    return buttonLink;
  }

  if (props.doc.type === "cms-pathway-step" && csContext.currentTeam?.id) {
    return `/team/dashboard/${csContext.currentTeam.id}`;
  }

  if (props.doc.type === "cms-personal-pathway-step") {
    return "/home?view=MyDashboard";
  }

  return "/";
};

const completeLocation = (): string => {
  if (isLastStepOfPathway.value && pathway.value) {
    return pathway.value.type === "cms-pathway" ? "/team/complete" : "/my/complete";
  }

  return closeLocation();
};

const close = (): void => {
  window.location.href = closeLocation();
};

const completeStep = async (): Promise<void> => {
  const delayPromise = delay(3000);

  try {
    completed.value = true;
    await logAsCompleted();
  } catch (e) {
    error.value = true;
    completed.value = false;
    throw e;
  }

  await delayPromise;
  window.location.href = completeLocation();
};

const handleComplete = async (): Promise<void> => {
  error.value = false;
  const canComplete = canCompletePathwayStep.value;

  if (canComplete && !pathwayIsCurrent.value && props.doc.type === "cms-personal-pathway-step") {
    isGoalModalDisplayed.value = true;
  } else if (canComplete) {
    await completeStep();
  }
};

const handleModalHide = async (msg: "ok" | "cancel"): Promise<void> => {
  if (msg === "ok") {
    isGoalModalDisplayed.value = false;
    try {
      await selectPathway();
      pathwayIsCurrent.value = true;
      await completeStep();
    } catch (e) {
      error.value = true;
    }
  }
  isGoalModalDisplayed.value = false;
};

const completeButtonText = computed<string>(() => {
  if (error.value) {
    return "Oops, please try again";
  }
  return buttonText.value ?? "Close";
});

const visual = ref<InstanceType<typeof CmsImg> | InstanceType<typeof CmsLottieAnimation> | null>();
const slide = ref<InstanceType<typeof SessionSlide>>();
const visualDiv = computed(() => slide?.value?.visualDiv);

defineExpose({
  visualDiv,
  visual,
  slide,
});

onBeforeMount(async () => {
  if (csContext.currentTeam?.id) {
    await isTeamMember(csContext.currentTeam.id);
  }
  if (props.doc.type === "cms-pathway-step" || props.doc.type === "cms-personal-pathway-step") {
    await loadPathwayData();
    ready.value = true;
  } else {
    ready.value = true;
  }
});
</script>
